import { validateRut, cleanRut as rutlibCleanRut, formatRut } from "rutlib/lib";
import { PRICING } from "./constants";
import { DISC_STYLE_NAMES, COLOR_NAME_TO_ACRONYM } from "../utils/competency-catalog";

export const formatCLP = (amount) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP" }).format(amount ?? 0);

export const computeIvaBreakdown = (totalWithIva) => {
  const total = Math.round(totalWithIva || 0);
  const subtotal = Math.round(total / (1 + PRICING.IVA_RATE));
  const iva = total - subtotal;
  return { subtotal, iva, total };
};

export const computeOriginalPrice = (plan, baseUnitPrice) => {
  const discount = plan?.discountPercentage ?? 0;
  if (discount <= 0 || discount >= 100) return plan?.totalPrice ?? 0;
  if (baseUnitPrice > 0) return baseUnitPrice * (plan?.testsQuantity ?? 0);
  return Math.round((plan?.totalPrice ?? 0) / (1 - discount / 100));
};

export const tripleDot = (name, maxLength) => name.length <= maxLength ? name : `${name.slice(0, maxLength - 1)}...`;

export const statusTranslator = (status) => {
  const allStates = {
    0: 'Sin enviar',
    1: 'Enviado',
    2: 'Enviado',
    3: 'Abierto',
    4: 'Finalizado',
    5: 'Evaluado',
  };
  return allStates[status] || status;
};

export const clientStatusTranslator = (status) => {
  const allStates = {
    PENDING: 'Pendiente',
    SENT: 'Enviado',
    OPENED: 'Abierto',
    FINISHED: 'Realizado',
    EVALUATED: 'Evaluado',
  };
  return allStates[status] || status;
};

export const statusColors = (status) => {
  const allStates = {
    0: 'gray',
    1: 'blue',
    2: 'blue',
    3: 'green',
    4: 'green',
    5: 'green',
  };
  return allStates[status] || 'white';
};

export const acronymChanger = (acronym) => {
  const allAcronyms = {
    'AM': 'I',
    'AZ': 'P',
    'R': 'R',
    'V': 'C',
  };
  return allAcronyms[acronym] || acronym;
}

// Los cuatro estilos DISC salen de la fuente única (Singulares/front#40); acá sólo
// viven los acrónimos de motivadores, que no son estilos DISC.
export const acronymInterpreter = (acronym) => {
  const motivators = {
    'EST': 'Estética',
    'REC': 'Principio rector',
    'AY': 'Ayuda a otros',
    'DES': 'Conocimiento y descubrimiento',
    'LID': 'Liderazgo',
    'PAZ': 'Paz y armonía',
    'INV': 'Retorno a la inversión',
  };
  return DISC_STYLE_NAMES[acronym] || motivators[acronym] || acronym;
};

export const colorInterpreter = (colors) => {
  const acronym = COLOR_NAME_TO_ACRONYM[colors];
  return acronym ? DISC_STYLE_NAMES[acronym] : colors;
}

export const calculatePercentMotivators = (competences) => {
  const colorCounts = {
    amarillo: 0,
    azul: 0,
    rojo: 0,
    verde: 0
  };

  const totalCompetences = competences.length;

  competences.forEach(comp => {
    comp.colorType.forEach(color => {
      const lowercaseColor = color.toLowerCase();
      colorCounts[lowercaseColor]++;
    });
  });

  const percentColorCount = [];

  for (const color in colorCounts) {
    percentColorCount.push({
      name: color,
      value: (colorCounts[color] * 100) / totalCompetences
    });
  }

  return percentColorCount
}

export const validatePhone = phone => {
  if (!phone) return true;
  return (/^9[0-9].{7}$/i).test(phone);
}

export const newValidatePhone = (phone) => {
  if (!phone) return true;
  return (/^\+[0-9]{1,19}$/).test(phone);
}
export const validateEmail = (value) => value ? !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value) : undefined;

export const validRut = (rut) => {
  if (rut.length < 8) {
    return false;
  }
  return validateRut(rut);
}

export const cleanRut = (rut) => rutlibCleanRut(rut);

// Formatea un RUT a medida que se escribe (XX.XXX.XXX-X). Evita mostrar un
// guión inicial cuando aún hay muy pocos dígitos ingresados.
export const formatRutInput = (value) => {
  const clean = rutlibCleanRut(value || "");
  if (!clean) return "";
  if (clean.length < 2) return clean;
  return formatRut(clean);
}

export const normalizeString =(str) => {
  const from = "áàäâãåçéèëêíìïîñóòöôõúùüûÁÀÄÂÃÅÇÉÈËÊÍÌÏÎÑÓÒÖÔÕÚÙÜÛ";
  const to   = "aaaaaaceeeeiiiinooooouuuuAAAAAACEEEEIIIINOOOOOUUUU";
  const mapping = {};

  for (let i = 0; i < from.length; i++) {
      mapping[from.charAt(i)] = to.charAt(i);
  }

  const removeAccents = (text) => {
      return text.split('').map(char => mapping[char] || char).join('');
  };

  const removeSpecialChars = (text) => {
      return text.replace(/[^a-zA-Z0-9]/g, '');
  };

  const normalized = removeAccents(str);
  const cleaned = removeSpecialChars(normalized);
  return cleaned;
}


export const colorsFromResults = (results, isBoundedDisc, isTestGrowmeResult) => {
  const translate = {
    AM: "amarillo",
    AZ: "azul",
    V: "verde",
    R: "rojo",
  };

  let sectionScore;

  if (Array.isArray(results) && results.length === 4 && !isTestGrowmeResult) {
    sectionScore = [...results[1].sectionScore];
  }

  if (Array.isArray(results) && results.length === 1 && isBoundedDisc && !isTestGrowmeResult) {
    sectionScore = [...results[0].sectionScore];
  }

  if (!Array.isArray(results) && results.resultTestDisc && isTestGrowmeResult) {
    sectionScore = [...results.resultTestDisc[0].sectionScore];
  }

  if (sectionScore) {
    const orden = sectionScore.sort((a, b) => b.DISC - a.DISC);
    const response = [
      translate[orden[0].indicatorAcronym],
      translate[orden[1].indicatorAcronym],
    ];
    return response;
  }

  return ['none', 'none'];
};

